import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { useOsDetalhe, useOsTransicao } from '../../hooks/useOs'
import AtividadesOS from './AtividadesOS'
import EvidenciasOS from './EvidenciasOS'
import styles from '../_shared/CrudPage.module.css'

const STATUS_LABEL: Record<string, string> = {
  aberta: 'Aberta',
  atribuida: 'Atribuída',
  em_execucao: 'Em Execução',
  concluida: 'Concluída',
  aprovada: 'Aprovada',
  cancelada: 'Cancelada',
}

const STATUS_COR: Record<string, string> = {
  aberta: '#1565C0',
  atribuida: '#7B1FA2',
  em_execucao: '#F57C00',
  concluida: '#388E3C',
  aprovada: '#00897B',
  cancelada: '#616161',
}

function extractErrorMsg(err: unknown): string {
  const e = err as { response?: { data?: { status?: string; detail?: string } } }
  return (
    e?.response?.data?.status ?? e?.response?.data?.detail ?? 'Erro ao executar transição.'
  )
}

export default function OrdemServicoDetalhePage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: os, isLoading, error } = useOsDetalhe(id)
  const { atribuir, iniciar, concluir, aprovar, cancelar } = useOsTransicao(id)

  const [motivoCancelamento, setMotivoCancelamento] = useState('')
  const [mostrandoCancelar, setMostrandoCancelar] = useState(false)

  if (isLoading) return <div className={styles.loading}>Carregando ordem de serviço...</div>
  if (error || !os) return <div className={styles.empty}>OS não encontrada.</div>

  async function transicionar(
    fn: () => Promise<unknown>,
    sucessoMsg: string,
  ): Promise<void> {
    try {
      await fn()
      notifications.show({ color: 'green', message: sucessoMsg })
    } catch (err) {
      notifications.show({ color: 'red', title: 'Erro', message: extractErrorMsg(err) })
    }
  }

  async function handleCancelar() {
    if (!motivoCancelamento.trim()) {
      notifications.show({ color: 'orange', message: 'Informe o motivo do cancelamento.' })
      return
    }
    await transicionar(
      () => cancelar.mutateAsync(motivoCancelamento.trim()),
      'OS cancelada.',
    )
    setMostrandoCancelar(false)
    setMotivoCancelamento('')
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            onClick={() => navigate('/ordens-servico')}
            type="button"
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>OS {os.numero ?? id.slice(0, 8)}</h1>
          <span
            data-testid="os-status-badge"
            style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              borderRadius: '4px',
              backgroundColor: STATUS_COR[os.status] ?? '#888',
              color: 'white',
              fontSize: '0.85rem',
              marginTop: '0.5rem',
            }}
          >
            {STATUS_LABEL[os.status] ?? os.status}
          </span>
        </div>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Dados</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.5rem 1rem' }}>
          <dt>Título</dt>
          <dd>{os.titulo}</dd>
          <dt>Tipo</dt>
          <dd>{os.tipo}</dd>
          <dt>Prioridade</dt>
          <dd>{os.prioridade}</dd>
          {os.descricao && (
            <>
              <dt>Descrição</dt>
              <dd>{os.descricao}</dd>
            </>
          )}
          {os.aberta_em && (
            <>
              <dt>Aberta em</dt>
              <dd>{new Date(os.aberta_em).toLocaleString('pt-BR')}</dd>
            </>
          )}
          {os.iniciada_em && (
            <>
              <dt>Iniciada em</dt>
              <dd>{new Date(os.iniciada_em).toLocaleString('pt-BR')}</dd>
            </>
          )}
          {os.concluida_em && (
            <>
              <dt>Concluída em</dt>
              <dd>{new Date(os.concluida_em).toLocaleString('pt-BR')}</dd>
            </>
          )}
        </dl>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Ações</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {os.status === 'aberta' && (
            <button
              type="button"
              data-testid="btn-atribuir"
              className={styles.btnPrimary}
              onClick={() =>
                transicionar(() => atribuir.mutateAsync(undefined), 'OS atribuída.')
              }
              disabled={atribuir.isPending}
            >
              Atribuir
            </button>
          )}
          {(os.status === 'aberta' || os.status === 'atribuida') && (
            <button
              type="button"
              data-testid="btn-iniciar"
              className={styles.btnPrimary}
              onClick={() => transicionar(() => iniciar.mutateAsync(), 'OS iniciada.')}
              disabled={iniciar.isPending}
            >
              ▶ Iniciar
            </button>
          )}
          {os.status === 'em_execucao' && (
            <button
              type="button"
              data-testid="btn-concluir"
              className={styles.btnPrimary}
              onClick={() => transicionar(() => concluir.mutateAsync(), 'OS concluída.')}
              disabled={concluir.isPending}
            >
              ✓ Concluir
            </button>
          )}
          {os.status === 'concluida' && (
            <button
              type="button"
              data-testid="btn-aprovar"
              className={styles.btnPrimary}
              onClick={() => transicionar(() => aprovar.mutateAsync(), 'OS aprovada.')}
              disabled={aprovar.isPending}
            >
              ✓✓ Aprovar
            </button>
          )}
          {!['aprovada', 'cancelada'].includes(os.status) && (
            <button
              type="button"
              data-testid="btn-cancelar"
              onClick={() => setMostrandoCancelar(true)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #C62828',
                background: 'white',
                color: '#C62828',
                cursor: 'pointer',
                borderRadius: '4px',
              }}
            >
              Cancelar OS
            </button>
          )}
        </div>

        {/* placeholder: Evidências carregadas abaixo */}
        <div style={{ height: 0 }} />
        {mostrandoCancelar && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#FFF3E0', borderRadius: '4px' }}>
            <label htmlFor="motivo-cancelar" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Motivo do cancelamento *
            </label>
            <textarea
              id="motivo-cancelar"
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '0.5rem' }}
              data-testid="motivo-cancelar-textarea"
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleCancelar}
                disabled={cancelar.isPending}
                data-testid="btn-confirmar-cancelar"
              >
                Confirmar cancelamento
              </button>
              <button
                type="button"
                onClick={() => {
                  setMostrandoCancelar(false)
                  setMotivoCancelamento('')
                }}
              >
                Voltar
              </button>
            </div>
          </div>
        )}
      </section>

      <AtividadesOS
        ordemServicoId={id}
        readonly={['aprovada', 'cancelada'].includes(os.status)}
      />

      <EvidenciasOS
        ordemServicoId={id}
        readonly={['aprovada', 'cancelada'].includes(os.status)}
      />
    </div>
  )
}
